import * as React from "react";
import { Link } from "react-router-dom";
import { CustomCalculation } from "webportalclient";
import { MatchSortHeader } from "../../Components/MatchSortHeader";
import { ODataParams, Query, useQuery } from "../../Components/Query";
import { Column, Table } from "../../Components/QueryTable";
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";
import { useSecurityContext } from "../../Core/SecurityContext";
import { Switch } from "../../forms/components/Switch";
import { ActionButtons } from "../../misc/ActionButtons";
import { PermissionEnum } from "../../misc/api/ApiPermissionEnum";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import { Authorization } from "../../misc/components/Authorization";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";

const CustomCalculationsTable = () => {
    const webPortalClient = useWebPortalApiClient();
    const { data, applyFilters, filters, refresh } = useQuery();

    const onActiveToggle = async (calculation: CustomCalculation) => {
        await webPortalClient.client.patchCustomCalculation({
            active: !calculation.active
        }, calculation.id);
        refresh();
    }

    return (
        <Table data={data}>
            <Column field="pluginName" header={<MatchSortHeader title="Type" field="pluginName" options={[]} sortField="pluginName" />} formatter={pn => pn} />
            <Column field="" header={<MatchSortHeader title="Active" field="pluginname" options={[]} sortField="pluginname" />} formatter={ns => <Switch isToggledOn={ns.active} onChange={() => onActiveToggle(ns)} />} />
        </Table>
    );
}

export const CustomCalculationsScreen = () => {
    const [alertShown, setAlertShown] = React.useState<boolean>(false);
    const [errorAlert, setErrorAlert] = React.useState<string | undefined>();
    const [submitting, setSubmitting] = React.useState<boolean>(false);
    const legacyClient = useWebPortalLegacyClient();
    const webPortalClient = useWebPortalApiClient();
    const [calculationPlugins, setCalculationPlugins] = React.useState<undefined | any[]>();
    const [selectedPlugins, setSelectedPlugins] = React.useState([]);
    const { currentUserPermissions } = useSecurityContext();
    const { study } = useCurrentStudyConfigurationContext();

    React.useEffect(() => {
        const getPlugins = async () => {
            const result = await legacyClient.getCalculationPlugins();
            setCalculationPlugins(result);
        }

        getPlugins();
    }, []);

    const handleRefresh = async (params: ODataParams) => {
        return await webPortalClient.client.getCustomCalculations(undefined, undefined, params.orderby, undefined, params.top, params.skip, undefined, true);
    }

    const onSave = async () => {
        setSubmitting(true);
        try {
            for (const plugin of selectedPlugins) {
                await webPortalClient.client.postCustomCalculation({
                    pluginId: plugin.Id,
                    pluginName: plugin.Name,
                    active: true
                });
            }
        } catch (ex) {
            setErrorAlert(ex.message);
        }
        setSubmitting(false);
    }

    const onCalculationSelect = async (e) => {
        const selectedOptions = [...e.target.options].filter(o => o.selected);
        const pluginSelected = calculationPlugins.filter(ft => selectedOptions.map(x => x.label).includes(ft.Name));
        setSelectedPlugins(pluginSelected);
    }

    if (!calculationPlugins)
        return <LoadingWithMessage message="Loading Calculations..." />

    return (
        <>
            <Authorization studyId={study.id} currentPermissions={currentUserPermissions} permissionId={PermissionEnum.ManageCustomCalculations}>
                <Alert show={alertShown} category={AlertCategoryEnum.Success}>
                    Saved successfully. Return to{" "}
                    <Link to={`/studies`}>Studies list</Link>?
                </Alert>
                <Alert show={errorAlert !== undefined} category={AlertCategoryEnum.Danger}>
                    {errorAlert}
                </Alert>
                <div className="row">
                    <div className="col-md-12">
                        <h2 className="text-primary">Custom Calculations</h2>
                        <div className="panel panel-default" data-automationid="CustomCalculationsPanel">
                            <div className="panel-body">
                                <div className="row">
                                    <div className="col-md-12">
                                        <h3 className="text-primary">Available Calculations</h3>
                                        <Alert show={calculationPlugins.length === 0} category={AlertCategoryEnum.Danger}>
                                            There are no custom calculations installed on this instance. Please contact an administrator if this is unexpected.
                                        </Alert>
                                        {calculationPlugins.length > 0 ? <select multiple className="form-control" id="selectedCalculation" onChange={onCalculationSelect}>
                                            {calculationPlugins.map(cp => (<option key={cp.Name}>{cp.Name}</option>))}
                                        </select> : null}
                                    </div> 
                                </div>
                                <div className="row">
                                    <div className="col-md-12">
                                        <button className="btn btn-primary pull-right" onClick={() => onSave()}>Install</button>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-12">
                                        <h3 className="text-primary">Installed Calculations</h3>
                                        {submitting ? <LoadingWithMessage message="Loading Installed Calculations..." /> : <Query onRefresh={handleRefresh}>
                                            <CustomCalculationsTable />
                                        </Query>}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-md-12">
                                <ActionButtons submitting={submitting} onSaveClick={() => onSave()} automationIdPrefix="ActionsHeader" returnToListRoute="/studies" />
                            </div>
                        </div>
                    </div>
                </div>
            </Authorization>
        </>
    );
}
